import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Chip,
  Alert,
  Grid,
  InputAdornment,
  Tooltip,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Folder as FolderIcon,
  Assessment as AssessmentIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { performanceService } from '../../services/performanceService';
import { isAdmin } from '../../services/authService';

const ProjectsTab = ({ onViewProjectExecutions }) => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    projectName: '',
    description: ''
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    // Filter projects based on search query
    if (searchQuery.trim() === '') {
      setFilteredProjects(projects);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = projects.filter(project =>
        project.projectName.toLowerCase().includes(query) ||
        project.description.toLowerCase().includes(query) ||
        (project.createdBy?.name && project.createdBy.name.toLowerCase().includes(query)) ||
        (project.createdBy?.email && project.createdBy.email.toLowerCase().includes(query))
      );
      setFilteredProjects(filtered);
    }
  }, [searchQuery, projects]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await performanceService.getProjects();
      setProjects(response.data);
      setFilteredProjects(response.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch projects');
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (project = null) => {
    setEditingProject(project);
    setFormData({
      projectName: project ? project.projectName : '',
      description: project ? project.description : ''
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProject(null);
    setFormData({ projectName: '', description: '' });
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingProject) {
        await performanceService.updateProject(editingProject._id, formData);
      } else {
        await performanceService.createProject(formData);
      }
      handleCloseDialog();
      fetchProjects();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save project');
    }
  };

  const handleDelete = async (projectId) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await performanceService.deleteProject(projectId);
        fetchProjects();
      } catch (err) {
        setError('Failed to delete project');
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate metrics
  const totalProjects = projects.length;
  const totalExecutions = projects.reduce((sum, p) => sum + (p.executionCount || 0), 0);

  // Metric Card Component
  const MetricCard = ({ title, value, icon, color, subtitle }) => (
    <Card
      sx={{
        height: '100%',
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        border: `1px solid ${color}30`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 24px ${color}20`
        }
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 600, color: color, mb: 1 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="textSecondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar sx={{ bgcolor: color, width: 56, height: 56 }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return <Typography>Loading projects...</Typography>;
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Summary Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <MetricCard
            title="TOTAL PROJECTS"
            value={totalProjects}
            icon={<FolderIcon sx={{ fontSize: 28 }} />}
            color="#1976d2"
            subtitle="All projects"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MetricCard
            title="TOTAL EXECUTIONS"
            value={totalExecutions}
            icon={<AssessmentIcon sx={{ fontSize: 28 }} />}
            color="#ed6c02"
            subtitle="Across all projects"
          />
        </Grid>
      </Grid>

      {/* Header with Search and Create Button */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} gap={2} flexWrap="wrap">
        <TextField
          placeholder="Search projects..."
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            minWidth: 300,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        {isAdmin() && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3
            }}
          >
            Create Project
          </Button>
        )}
      </Box>

      {/* Projects Grid */}
      {filteredProjects.length === 0 ? (
        <Card sx={{ p: 6, textAlign: 'center', bgcolor: 'grey.50' }}>
          <FolderIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            {searchQuery ? 'No projects found' : 'No projects yet'}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {searchQuery
              ? 'Try adjusting your search query'
              : 'Create your first project to get started with performance testing'
            }
          </Typography>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {filteredProjects.map((project) => (
            <Grid item xs={12} sm={6} md={4} key={project._id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  overflow: 'visible',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)',
                    borderRadius: '4px 4px 0 0'
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', pt: 3 }}>
                  {/* Project Header with Actions */}
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box sx={{ flexGrow: 1, pr: 1 }}>
                      <Typography
                        variant="h5"
                        gutterBottom
                        sx={{
                          fontWeight: 700,
                          color: 'text.primary',
                          mb: 1,
                          lineHeight: 1.2
                        }}
                      >
                        {project.projectName}
                      </Typography>
                    </Box>
                    {isAdmin() && (
                      <Box display="flex" gap={0.5}>
                        <Tooltip title="Edit Project">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(project)}
                            sx={{
                              bgcolor: 'action.hover',
                              '&:hover': {
                                bgcolor: 'primary.main',
                                color: 'white'
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Project">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(project._id)}
                            sx={{
                              bgcolor: 'action.hover',
                              '&:hover': {
                                bgcolor: 'error.main',
                                color: 'white'
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>

                  {/* Execution Count Badge */}
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onViewProjectExecutions && project.executionCount > 0) {
                        onViewProjectExecutions(project._id);
                      }
                    }}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      bgcolor: 'primary.50',
                      border: '1px solid',
                      borderColor: 'primary.200',
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      mb: 2,
                      width: 'fit-content',
                      cursor: project.executionCount > 0 ? 'pointer' : 'default',
                      transition: 'all 0.2s',
                      '&:hover': project.executionCount > 0 ? {
                        bgcolor: 'primary.100',
                        borderColor: 'primary.400',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)'
                      } : {}
                    }}
                  >
                    <AssessmentIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: 'primary.main'
                      }}
                    >
                      {project.executionCount || 0} Executions
                    </Typography>
                  </Box>

                  {/* Project Description */}
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 3,
                      flexGrow: 1,
                      color: 'text.secondary',
                      lineHeight: 1.6,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical'
                    }}
                  >
                    {project.description}
                  </Typography>

                  {/* Project Footer */}
                  <Box
                    sx={{
                      pt: 2,
                      mt: 'auto',
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 1.5
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          bgcolor: 'primary.light',
                          fontSize: '0.75rem'
                        }}
                      >
                        {(project.createdBy?.name || project.createdBy?.email || 'U').charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        {project.createdBy?.name || project.createdBy?.email || 'Unknown'}
                      </Typography>
                    </Box>
                    <Chip
                      icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
                      label={formatDate(project.createdDate)}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderRadius: 1,
                        height: 24,
                        '& .MuiChip-label': {
                          px: 1,
                          fontSize: '0.75rem'
                        }
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create/Edit Project Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingProject ? 'Edit Project' : 'Create New Project'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="projectName"
            label="Project Name"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.projectName}
            onChange={handleInputChange}
            required
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            name="description"
            label="Description"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={formData.description}
            onChange={handleInputChange}
            required
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.projectName || !formData.description}
          >
            {editingProject ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectsTab;
